/**
 * Date Utility Functions
 * Centralized date formatting for consistent display across the application
 */

import dayjs from 'dayjs';

/**
 * Format date to DD-MM-YYYY format
 * @param {string|Date|dayjs.Dayjs} date - Date to format
 * @returns {string} Formatted date string (DD-MM-YYYY) or '-' if invalid
 */
export const formatDate = (date) => {
  if (!date) return '-';
  
  try {
    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) return '-';
    return parsedDate.format('DD-MM-YYYY');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

/**
 * Format date to DD-MM-YYYY HH:mm format (with time)
 * @param {string|Date|dayjs.Dayjs} date - Date to format
 * @returns {string} Formatted date string (DD-MM-YYYY HH:mm) or '-' if invalid
 */
export const formatDateTime = (date) => {
  if (!date) return '-';
  
  try {
    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) return '-';
    return parsedDate.format('DD-MM-YYYY HH:mm');
  } catch (error) {
    console.error('Error formatting date time:', error);
    return '-';
  }
};

/**
 * Parse date string to dayjs object
 * @param {string|Date} date - Date string or Date object
 * @returns {dayjs.Dayjs|null} Parsed dayjs object or null if invalid
 */
export const parseDate = (date) => {
  if (!date) return null;
  
  try {
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed : null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * Validate that a date is strictly in the future (after today).
 * Compares date-only (strip time) to avoid timezone bugs.
 * @param {string|Date|dayjs.Dayjs} date - Date to validate (e.g. YYYY-MM-DD or ISO string)
 * @returns {boolean} true if date is after today (date-only), false if today, past, or invalid
 */
export const validateFollowUpDate = (date) => {
  if (!date) return false;
  const selected = dayjs(date).startOf('day');
  const today = dayjs().startOf('day');
  if (!selected.isValid()) return false;
  return selected.isAfter(today);
};
